import { useAuth0 } from '@auth0/auth0-react'
import jwtDecode from 'jwt-decode'
import { useEffect, useState } from 'react'

import { APP_ENV } from 'src/env'
import { handlePromise } from 'src/utils/handlePromise'
import { useLocalStorage } from 'src/utils/hooks/useLocalStorage'
import { IS_CYPRESS } from 'src/utils/misc'

import logger from '../logger'

export const AuthEnum = {
    loading: 'loading',
    error: 'error',
    anonymous: 'anonymous',
    user: 'user',
    orgUser: 'orgUser',
    cypress: 'cypress',
    ttxUser: 'ttxUser',
}

/**
 * @typedef {Object} State
 * @property {keyof AuthEnum} State.authState
 * @property {import('@auth0/auth0-react').User} State.auth0User
 * @property {string} State.userEmail
 * @property {string} State.authToken
 * @property {Error} State.authTokenError
 */

/**
 * @returns {State}
 */
const useAuthState = (organizationId) => {
    /**
     * @type {ReturnType <typeof useState<keyof AuthEnum>>}
     */
    const [auth, setAuth] = useState(AuthEnum.loading)

    /**
     * @type {ReturnType <typeof useState<import('@auth0/auth0-react').User>>}
     */
    const [auth0User, setAuth0User] = useState(null)

    /**
     * @type {ReturnType <typeof useState<string>>}
     */
    const [userEmail, setUserEmail] = useState(null)

    /**
     * @type {ReturnType <typeof useState<string>>}
     */
    const [token, setToken] = useState(null)

    useLocalStorage(token, 'access_token')

    /**
     * @type {ReturnType <typeof useState<Error>>}
     */
    const [authTokenError, setAuthTokenError] = useState(null)

    const { isLoading, error, user, getAccessTokenSilently, isAuthenticated } =
        useAuth0()

    useEffect(() => {
        if (isLoading) {
            setAuth(AuthEnum.loading)
            return
        }

        if (error) {
            logger.error('auth0sdkerror', error)
            setAuth(AuthEnum.error)
            return
        }

        if (IS_CYPRESS) {
            setAuth(AuthEnum.cypress)
            return
        }

        if (!isAuthenticated) {
            setAuth(AuthEnum.anonymous)
            return
        } else {
            setAuth(AuthEnum.user)
            if (organizationId) {
                setAuth(AuthEnum.orgUser)
            }
        }
    }, [error, isAuthenticated, isLoading, organizationId])

    useEffect(() => {
        async function runGetToken() {
            const [tokenFetchError, token] = await handlePromise(
                getAccessTokenSilently({
                    audience: APP_ENV.REACT_APP_AUTH0_AUDIENCE,
                    scope: 'openid profile email',
                })
            )

            if (tokenFetchError) {
                logger.error(`getAccessTokenSilently failed`, tokenFetchError)
                setAuthTokenError(
                    new Error(
                        `getAccessTokenSilentlyFailed\n${JSON.stringify(tokenFetchError)}`
                    )
                )
            } else {
                //TODO: handle possible errors here

                /**
                 * @type {ReturnType <typeof jwtDecode<string>>}
                 */
                const decodedToken = jwtDecode(token)
                const email = decodedToken[`https://www.orna.app/email`]
                setUserEmail(email)
                setToken(token)
            }
        }

        if (isAuthenticated) {
            runGetToken()
        }
    }, [getAccessTokenSilently, isAuthenticated])

    useEffect(() => {
        if (user) {
            setAuth0User(user)
        }
    }, [user])

    return {
        authState: auth,
        auth0User,
        userEmail,
        authToken: token,
        authTokenError,
    }
}

export default useAuthState
